import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, X, Send, Phone, Mail, Calculator, PackageSearch, PhoneCall } from "lucide-react";
import { useBrand, LogoMark } from "../../context/BrandContext";
import { getSessionId, fetchHistory, streamChat } from "../../lib/chatApi";
import { Bubble } from "./ChatMessages";
import { CallbackForm } from "./CallbackForm";

const CHIPS = ["How does payment work?", "Open vs enclosed?", "Can you deliver to my mechanic?", "Get a price estimate", "Track my shipment"];
const FALLBACK = "Sorry, I couldn't connect just now. Please call or email our team.";

const QuickActions = ({ b, close }) => (
  <div className="grid grid-cols-4 gap-1.5 px-3 py-2 border-b border-slate-200 bg-white" data-testid="chat-quick-actions">
    <a href={`tel:${b.phone_tel}`} className="flex flex-col items-center gap-0.5 py-1.5 text-[11px] text-slate-700 hover:bg-amber-50" data-testid="chat-action-call"><Phone className="w-4 h-4 text-orange-600" />Call</a>
    <a href={`mailto:${b.email}`} className="flex flex-col items-center gap-0.5 py-1.5 text-[11px] text-slate-700 hover:bg-amber-50" data-testid="chat-action-email"><Mail className="w-4 h-4 text-orange-600" />Email</a>
    <Link to={{ pathname: "/", hash: "#quote" }} onClick={close} className="flex flex-col items-center gap-0.5 py-1.5 text-[11px] text-slate-700 hover:bg-amber-50" data-testid="chat-action-quote"><Calculator className="w-4 h-4 text-orange-600" />Get a Quote</Link>
    <Link to="/track" onClick={close} className="flex flex-col items-center gap-0.5 py-1.5 text-[11px] text-slate-700 hover:bg-amber-50" data-testid="chat-action-track"><PackageSearch className="w-4 h-4 text-orange-600" />Track</Link>
  </div>
);

export const ChatWidget = () => {
  const b = useBrand();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [input, setInput] = useState("");
  const [callback, setCallback] = useState(false);
  const sid = useRef(null);
  const endRef = useRef(null);
  const close = () => setOpen(false);

  useEffect(() => {
    if (!open || loaded) return;
    sid.current = getSessionId();
    fetchHistory(sid.current).then((m) => setMessages(m.map((x) => ({ ...x })))).catch(() => {}).finally(() => setLoaded(true));
  }, [open, loaded]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [messages, callback, open]);

  const patchLast = (fn) => setMessages((ms) => ms.map((m, i) => (i === ms.length - 1 ? fn(m) : m)));

  const send = async (text) => {
    const t = text.trim();
    if (!t || busy) return;
    setInput(""); setBusy(true);
    setMessages((ms) => [...ms, { role: "user", content: t }, { role: "assistant", content: "", cards: [], pending: true }]);
    try {
      await streamChat(sid.current, t, (ev) => {
        if (ev.type === "cards") patchLast((m) => ({ ...m, cards: ev.cards }));
        else if (ev.type === "delta") patchLast((m) => ({ ...m, content: m.content + ev.text }));
        else if (ev.type === "error") patchLast((m) => ({ ...m, content: ev.message, error: true }));
      });
    } catch (e) {
      patchLast((m) => ({ ...m, content: e.message || FALLBACK, error: true }));
    } finally {
      patchLast((m) => ({ ...m, pending: false, content: m.content || FALLBACK, error: m.error || !m.content }));
      setBusy(false);
    }
  };

  const leadDone = (f) => {
    setCallback(false);
    setMessages((ms) => [...ms, { role: "assistant", content: `Thanks, ${f.name.split(" ")[0]}! Our team will call you at ${f.phone} as soon as possible. For anything urgent, call ${b.phone_display}.`, local: true }]);
  };

  const hasUser = messages.some((m) => m.role === "user");
  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} className="fixed bottom-5 right-5 z-[60] h-14 pl-4 pr-5 flex items-center gap-2 bg-amber-500 text-slate-900 font-display font-bold uppercase tracking-wider shadow-[0_12px_30px_-8px_rgba(234,88,12,.55)] hover:bg-amber-400 transition-colors rounded-full" data-testid="chat-launcher-button" aria-label="Open chat assistant">
          <MessageCircle className="w-5 h-5" /><span className="hidden sm:inline">Ask us</span>
        </button>
      )}
      {open && (
        <div className="fixed z-[70] inset-0 sm:inset-auto sm:bottom-5 sm:right-5 sm:w-[400px] sm:h-[min(660px,calc(100vh-2.5rem))] flex flex-col bg-[#F7F8FA] border border-slate-200 shadow-[0_24px_60px_-20px_rgba(15,23,42,.45)] rise" data-testid="chat-panel" role="dialog" aria-label="Echevarria Trucking Assistant">
          <header className="flex items-center gap-3 px-4 h-16 bg-white border-b border-slate-200 shrink-0">
            <span className="relative shrink-0"><LogoMark size="w-10 h-10" testid="chat-header-logo" /><span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white" /></span>
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold uppercase text-brand-600 tracking-wide leading-tight truncate">Echevarria Trucking Assistant</div>
              <div className="text-[11px] text-slate-500">Answers in seconds · Team on {b.phone_display}</div>
            </div>
            <button onClick={close} className="w-9 h-9 grid place-items-center text-slate-600 hover:bg-slate-100" aria-label="Close chat" data-testid="chat-close-button"><X className="w-5 h-5" /></button>
          </header>
          <QuickActions b={b} close={close} />
          <div className="flex-1 overflow-y-auto px-3 py-4 space-y-3" data-testid="chat-messages">
            <Bubble index="welcome" b={b} m={{ role: "assistant", content: `Hi! I'm the Echevarria Trucking Assistant. I can explain our services, payment and delivery, give you an instant price estimate, or check a tracking number.\n\nWhat can I help you with?` }} />
            {!hasUser && (
              <div className="flex flex-wrap gap-1.5" data-testid="chat-suggestions">
                {CHIPS.map((c, i) => (
                  <button key={c} onClick={() => send(c)} disabled={!loaded || busy} className="px-3 py-1.5 text-xs border border-amber-300 bg-white text-slate-800 hover:bg-amber-50 rounded-full disabled:opacity-50" data-testid={`chat-chip-${i}`}>{c}</button>
                ))}
              </div>
            )}
            {messages.map((m, i) => <Bubble key={i} index={i} m={m} b={b} onNavigate={close} />)}
            {callback && <CallbackForm sessionId={sid.current} onDone={leadDone} onClose={() => setCallback(false)} />}
            <div ref={endRef} />
          </div>
          <div className="bg-white border-t border-slate-200 p-3 shrink-0">
            {!callback && (
              <button onClick={() => setCallback(true)} className="mb-2 inline-flex items-center gap-1.5 text-xs font-semibold text-amber-800 hover:text-amber-900" data-testid="chat-request-callback-button"><PhoneCall className="w-3.5 h-3.5" />Request a callback</button>
            )}
            <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="flex gap-2">
              <input value={input} onChange={(e) => setInput(e.target.value)} maxLength={1000} placeholder="Type your question…" disabled={!loaded}
                className="flex-1 h-11 px-3 border border-slate-300 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500" data-testid="chat-input" aria-label="Message" />
              <button type="submit" disabled={busy || !input.trim()} className="btn-amber h-11 w-11 px-0 disabled:opacity-50" aria-label="Send" data-testid="chat-send-button"><Send className="w-4 h-4" /></button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};
